import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';
import { escape } from 'lodash';

export type InspectionLightboxSlide = {
  /** Shot label with its position, e.g. "03/12 Front left". */
  title: string;
  /** Recording date and agent, shown under the title. */
  meta?: string;
  imageUrl?: string;
  /** Shown in place of a photo, e.g. the skip reason. */
  notice?: { title: string; text?: string };
};

/** Assumed until a photo has loaded: inspection photos come from a 16:9 landscape stream. */
const DEFAULT_PHOTO_SIZE = { width: 3840, height: 2160 };

const toNoticeHtml = ({ title, text }: { title: string; text?: string }) =>
  `<div class="flex h-full items-center justify-center p-8">
    <div class="max-w-md rounded-lg bg-white px-5 py-4 text-sm text-obsidian-800">
      <p class="font-semibold">${escape(title)}</p>
      ${text ? `<p class="mt-1">${escape(text)}</p>` : ''}
    </div>
  </div>`;

/** Opens the photos of an Inspection in the PhotoSwipe lightbox (swipe, zoom, keyboard). */
export function openInspectionLightbox(slides: InspectionLightboxSlide[], index = 0) {
  const lightbox = new PhotoSwipeLightbox({
    dataSource: slides.map((slide) =>
      slide.imageUrl
        ? { src: slide.imageUrl, alt: slide.title, ...DEFAULT_PHOTO_SIZE, slide }
        : { html: toNoticeHtml(slide.notice ?? { title: slide.title }), slide },
    ),
    pswpModule: () => import('photoswipe'),
    wheelToZoom: true,
    bgOpacity: 1,
    showHideAnimationType: 'fade',
    returnFocus: false, // causes a weird maximum call stack size exceeded bug on IOS
    trapFocus: false, // causes a weird maximum call stack size exceeded bug on IOS
  });

  lightbox.on('uiRegister', () => {
    lightbox.pswp?.ui?.registerElement({
      name: 'inspection-caption',
      order: 9,
      isButton: false,
      appendTo: 'root',
      onInit: (element, pswp) => {
        element.className =
          'pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-6 pb-5 pt-10 text-center text-white';
        pswp.on('change', () => {
          const slide = pswp.currSlide?.data.slide as InspectionLightboxSlide | undefined;
          element.innerHTML = slide
            ? `<p class="font-semibold">${escape(slide.title)}</p>${
                slide.meta ? `<p class="text-sm text-white/70">${escape(slide.meta)}</p>` : ''
              }`
            : '';
        });
      },
    });
  });

  // Photos from devices whose stream is not 16:9 get their real size once loaded. Images may
  // finish preloading before they belong to a slide, so this hooks in when they are shown.
  lightbox.on('contentAppend', ({ content }) => {
    const image = content.element;
    if (!(image instanceof HTMLImageElement)) return;
    const fitToNaturalSize = () => {
      const { slide } = content;
      if (!slide || !image.naturalWidth) return;
      if (image.naturalWidth === content.width && image.naturalHeight === content.height) return;
      content.width = slide.width = image.naturalWidth;
      content.height = slide.height = image.naturalHeight;
      slide.calculateSize();
      slide.zoomAndPanToInitial();
      slide.applyCurrentZoomPan();
      slide.updateContentSize(true);
    };
    if (image.complete) requestAnimationFrame(fitToNaturalSize);
    else image.addEventListener('load', fitToNaturalSize, { once: true });
  });

  lightbox.on('destroy', () => {
    // The lightbox is single-use: release its listeners once closed.
    queueMicrotask(() => lightbox.destroy());
  });

  lightbox.init();
  void lightbox.loadAndOpen(index);
}
